import integrationsTranslations from './integrationsTranslations.js';
import en from './en.js';

/**
 * English is the universal fallback the whole app relies on (e.g. store.js'
 * `localizations[lang] || localizations.en` pattern), so it is the only language
 * bundled eagerly. Every other language is loaded on demand via dynamic import,
 * so Vite/Rollup code-splits each one into its own chunk that is fetched only
 * when a tenant uses that language. This keeps the other ~8 language files out
 * of the startup bundle.
 */
export const localizationLoaders = {
    es: () => import('./es.js'),
    it: () => import('./it.js'),
    fr: () => import('./fr.js'),
    sv: () => import('./sv.js'),
    pt: () => import('./pt.js'),
    de: () => import('./de.js'),
    pl: () => import('./pl.js'),
    da: () => import('./da.js'),
};

/**
 * Localizations present at boot: English (fallback) + integration strings. Other
 * languages are merged into the store by the LoadingProcess gate once loaded
 * (see `loadLocalization`). Exported as the default so
 * `import localizations from '...'` resolves and seeds the initial store state.
 */
const initialLocalizations = {
    integrations: integrationsTranslations,
    en,
};

export default initialLocalizations;

/**
 * Loads a single language's translations on demand. Returns `null` for the
 * eagerly-bundled fallback (`en`) and for unknown languages, since neither needs
 * an extra fetch.
 *
 * @param {string} lang - Language code (e.g. `es`, `de`).
 * @returns {Promise<object|null>} The language's translation object, or `null`.
 */
export async function loadLocalization(lang) {
    const loader = localizationLoaders[lang];
    if (!loader) return null;
    const module = await loader();
    return module.default;
}
